import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { checkout, validateCoupon, getConfig, getShippingQuote } from "../lib/api";
import Seo from "../components/Seo";
import { eur } from "../lib/format";
import { Truck, Landmark, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { name: "customer_name", label: "Nombre y apellidos *", required: true },
  { name: "company", label: "Empresa *", required: true },
  { name: "cif", label: "CIF / NIF *", required: true },
  { name: "email", label: "Email *", required: true, type: "email" },
  { name: "phone", label: "Teléfono *", required: true, type: "tel" },
  { name: "address", label: "Dirección de entrega *", required: true, full: true },
  { name: "postal_code", label: "Código postal *", required: true },
  { name: "city", label: "Población *", required: true },
  { name: "province", label: "Provincia *", required: true },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { customer } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ country: "España", observations: "" });
  const [submitting, setSubmitting] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [payMethod, setPayMethod] = useState("transferencia");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const submittedRef = useRef(false);

  useEffect(() => { getConfig().then(setCfg).catch(() => {}); }, []);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const r = await validateCoupon(coupon.trim(), subtotal);
      if (r.valid) { setApplied(r); toast.success(r.message); }
      else { setApplied(null); toast.error(r.message); }
    } catch { toast.error("No se pudo validar el cupón"); }
  };
  const couponDisc = applied ? applied.discount : 0;
  const _today = new Date().toISOString().slice(0, 10);
  const autoOn = cfg && cfg.auto_promo_enabled && cfg.auto_promo_threshold && subtotal >= cfg.auto_promo_threshold
    && (!cfg.auto_promo_start || cfg.auto_promo_start <= _today)
    && (!cfg.auto_promo_end || cfg.auto_promo_end >= _today);
  const autoDisc = autoOn ? (cfg.auto_promo_type === "percent" ? +(subtotal * cfg.auto_promo_value / 100).toFixed(2) : +(+cfg.auto_promo_value).toFixed(2)) : 0;
  const autoLabel = autoOn ? (cfg.auto_promo_label || "Promoción automática") : "";
  const discount = Math.min(couponDisc + autoDisc, subtotal);
  const base = Math.max(0, subtotal - discount);
  const [ship, setShip] = useState(null);
  useEffect(() => {
    if (!items.length) return;
    const t = setTimeout(() => {
      getShippingQuote({ items: items.map((i) => ({ product_id: i.product_id, name: i.name, reference: i.reference || "", price: i.price, quantity: i.quantity })), province: form.province || "", coupon_code: applied ? applied.code : "" })
        .then(setShip).catch(() => setShip(null));
    }, 400);
    return () => clearTimeout(t);
  }, [items, form.province, applied]);
  const shippingCost = ship && ship.status !== "consultar" ? ship.cost : 0;
  const taxable = base + shippingCost;

  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f, customer_name: customer.name || "", company: customer.company || "",
        cif: customer.cif || "", email: customer.email || "", phone: customer.phone || "",
        address: customer.address || "", postal_code: customer.postal_code || "",
        city: customer.city || "", province: customer.province || "",
      }));
    }
  }, [customer]);

  useEffect(() => {
    if (items.length === 0 && !submittedRef.current) nav("/carrito");
  }, [items.length, nav]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const fl of FIELDS) {
      if (fl.required && !String(form[fl.name] || "").trim()) {
        toast.error(`Complete: ${fl.label.replace(" *", "")}`);
        return;
      }
    }
    if (!acceptedTerms) {
      toast.error("Debe aceptar los Términos y condiciones y la Política de privacidad");
      return;
    }
    setSubmitting(true);
    try {
      const order = await checkout({
        ...form,
        coupon_code: applied ? applied.code : "",
        payment_method: payMethod,
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, reference: i.reference,
          price: i.price, quantity: i.quantity,
        })),
      });
      if (order.payment) {
        submittedRef.current = true;
        clear();
        const { url, fields } = order.payment;
        const f = document.createElement("form");
        f.method = "POST";
        f.action = url;
        Object.entries(fields).forEach(([n, v]) => {
          const inp = document.createElement("input");
          inp.type = "hidden"; inp.name = n; inp.value = v;
          f.appendChild(inp);
        });
        document.body.appendChild(f);
        f.submit();
        return;
      }
      submittedRef.current = true;
      localStorage.setItem("fp_last_order", JSON.stringify(order));
      nav("/pedido-confirmado", { state: { order } });
      clear();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el pedido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Seo title="Finalizar pedido | FABRIPAN" description="Introduzca sus datos para completar el pedido." noindex />
      <h1 className="font-heading font-bold text-4xl tracking-tighter mb-6">Finalizar pedido</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_340px] gap-8">
        <div className="space-y-6">
          <div className="border border-border rounded-sm bg-white p-5">
            <h2 className="font-heading font-semibold text-lg mb-4">Datos del cliente y entrega</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map((fl) => (
                <div key={fl.name} className={fl.full ? "sm:col-span-2" : ""}>
                  <label className="text-sm font-medium block mb-1">{fl.label}</label>
                  <input
                    type={fl.type || "text"}
                    value={form[fl.name] || ""}
                    onChange={(e) => set(fl.name, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid={`checkout-${fl.name}`}
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-sm font-medium block mb-1">Observaciones</label>
                <textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-observations" />
              </div>
            </div>
          </div>

          <div className="border border-border rounded-sm bg-white p-5">
            <h2 className="font-heading font-semibold text-lg mb-3">Forma de pago</h2>
            <div className="space-y-3">
              <button type="button" onClick={() => setPayMethod("transferencia")} className={`w-full flex items-start gap-3 border rounded-sm p-4 text-left transition-colors ${payMethod === "transferencia" ? "border-brand bg-brand/5" : "border-border hover:border-brand/40"}`} data-testid="pay-transferencia">
                <Landmark className={`h-6 w-6 shrink-0 ${payMethod === "transferencia" ? "text-brand" : "text-muted-foreground"}`} />
                <div>
                  <p className="font-medium">Transferencia bancaria</p>
                  <p className="text-sm text-muted-foreground">Tras confirmar el pedido le mostraremos las instrucciones con el IBAN y su número de pedido de referencia.</p>
                </div>
              </button>
              <button type="button" onClick={() => setPayMethod("tarjeta")} className={`w-full flex items-start gap-3 border rounded-sm p-4 text-left transition-colors ${payMethod === "tarjeta" ? "border-brand bg-brand/5" : "border-border hover:border-brand/40"}`} data-testid="pay-tarjeta">
                <CreditCard className={`h-6 w-6 shrink-0 ${payMethod === "tarjeta" ? "text-brand" : "text-muted-foreground"}`} />
                <div>
                  <p className="font-medium">Tarjeta (TPV seguro)</p>
                  <p className="text-sm text-muted-foreground">Pago con tarjeta a través de la pasarela segura de Redsys (BBVA). Será redirigido para completar el pago.</p>
                </div>
              </button>
            </div>
          </div>
        </div>

        <aside className="h-fit border border-border rounded-sm bg-white p-5 sticky top-24">
          <h2 className="font-heading font-semibold text-lg mb-4">Su pedido</h2>
          <div className="space-y-2 max-h-64 overflow-y-auto no-scrollbar mb-4">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm gap-2">
                <span className="text-muted-foreground line-clamp-1">{i.quantity} × {i.name}</span>
                <span className="font-mono shrink-0">{eur(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-3 mb-3">
            <label className="text-xs font-medium text-muted-foreground block mb-1">Código de descuento</label>
            <div className="flex gap-2">
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Ej: VERANO10" className="flex-1 px-3 py-2 text-sm border border-border rounded-sm font-mono" data-testid="coupon-input" />
              <button type="button" onClick={applyCoupon} className="bg-brand-ink text-white text-sm px-3 rounded-sm hover:bg-brand" data-testid="apply-coupon-btn">Aplicar</button>
            </div>
            {applied && <p className="text-xs text-emerald-600 mt-1" data-testid="coupon-applied">Cupón {applied.code} aplicado: −{eur(discount)}</p>}
          </div>
          <div className="border-t border-border pt-3 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Base imponible</span><span className="font-mono">{eur(subtotal)}</span></div>
            {couponDisc > 0 && <div className="flex justify-between text-sm text-emerald-600" data-testid="coupon-discount-line"><span>Cupón {applied.code}</span><span className="font-mono">−{eur(couponDisc)}</span></div>}
            {autoDisc > 0 && <div className="flex justify-between text-sm text-emerald-600" data-testid="auto-discount-line"><span>{autoLabel}</span><span className="font-mono">−{eur(autoDisc)}</span></div>}
            <div className="flex justify-between text-sm" data-testid="shipping-line">
              <span className="text-muted-foreground">Portes{ship && ship.weight_kg ? <span className="block text-[10px]">{ship.weight_kg} kg{ship.zone ? ` · zona ${ship.zone}` : ""}</span> : null}</span>
              <span className="font-mono text-right">
                {!form.province ? <span className="text-xs text-muted-foreground">indique provincia</span>
                  : !ship ? <span className="text-xs text-muted-foreground">calculando…</span>
                  : ship.status === "consultar" ? <span className="text-xs text-amber-700">a consultar</span>
                  : ship.status === "gratis" ? <span className="text-emerald-600">Gratis</span>
                  : eur(ship.cost)}
              </span>
            </div>
            {form.province && ship && ship.status === "consultar" && <p className="text-[11px] text-amber-700" data-testid="shipping-consult-note">Su provincia está fuera de la tarifa estándar. Le confirmaremos el importe de los portes antes del envío.</p>}
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">IVA (21%)</span><span className="font-mono">{eur(taxable * 0.21)}</span></div>
            <div className="flex justify-between font-heading font-bold text-lg"><span>Total</span><span className="text-brand">{eur(taxable * 1.21)}</span></div>
          </div>
          <label className="flex items-start gap-2 mt-5 text-xs text-muted-foreground cursor-pointer" data-testid="accept-terms-label">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-0.5 accent-brand h-4 w-4" data-testid="accept-terms-checkbox" />
            <span>He leído y acepto los <Link to="/pagina/terminos-condiciones" target="_blank" className="text-brand underline">Términos y condiciones</Link> y la <Link to="/pagina/politica-privacidad" target="_blank" className="text-brand underline">Política de privacidad</Link>.</span>
          </label>
          <button type="submit" disabled={submitting || !acceptedTerms} className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-full transition-colors" data-testid="confirm-order-btn">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Procesando...</> : "Confirmar pedido"}
          </button>
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Enviamos su pedido personalizado a su empresa.</p>
        </aside>
      </form>
    </div>
  );
}
